/**
 * The one error language every surface speaks (ticket #107/#108):
 * user-showable message extraction, shared by mobile today and by web
 * and the server copy in later tickets of the pass.
 *
 * Convex's dev client wraps rejections in a frame —
 * `[CONVEX M(lists:create)] [Request ID: …] Server Error\nUncaught
 * Error: <message>[ Called by client][\n  at handler (…)]` — so the
 * user-showable message is the LAST "Uncaught Error:" segment's own
 * line, with the frame, the "Called by client" decoration, collapsed
 * whitespace and any stack noise stripped. Frame-without-marker
 * shapes read past the frame line the same way. Production errors
 * carry the bare message, which passes through VERBATIM — the
 * server's copy is authored user-facing, so the full message (every
 * sentence, any line breaks) survives untouched.
 *
 * The few shapes we don't author map onto friendly copy: an expired
 * session ("Unauthenticated…") asks for a fresh sign-in, and
 * framework validator noise (never user-authored) collapses to a calm
 * generic line rather than leaking internals. Anything unparseable
 * never renders empty.
 */

export const GENERIC_ERROR_MESSAGE = "Something went wrong — please try again.";
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired — please sign in again.";
export const AUTH_FAILURE_MESSAGE = "Incorrect email or password.";

/**
 * The longest wait an auth call may keep a surface busy before it must
 * resolve to a definite state (ticket #334): every sign-in path's spinner
 * used to clear in a `finally` that only ran once the `signIn` promise
 * settled — and against a backend the client can't reach (a stale address
 * baked into an OTA bundle, a stalled WebSocket), the promise NEVER
 * settles, so no error copy ever appeared and the sheet spun forever.
 * A real failure (wrong password, capped rate) still settles fast and
 * keeps its own copy — this bound is only for the outcome that never
 * arrives at all. The copy must not name any host (the #204 law).
 */
export const AUTH_OUTCOME_TIMEOUT_MS = 15_000;
export const AUTH_UNREACHABLE_MESSAGE =
  "Can't reach the server — check your connection and try again.";

/** Races `op` against the bounded wait. The op's own rejection passes
 * through untouched — only a silent outcome maps onto the friendly
 * unreachable copy. A rejection arriving AFTER the bound won the race
 * (the client eventually noticing what the user already saw) is
 * deliberately swallowed — surfaced, it would be an unhandled rejection
 * the app must never crash on. */
export async function withAuthOutcome<T>(
  op: () => Promise<T>,
  timeoutMs: number = AUTH_OUTCOME_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const running = op();
  running.catch(() => undefined);
  try {
    return await Promise.race([
      running,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(AUTH_UNREACHABLE_MESSAGE)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The List-name and Item-title maxima the server's validation copy
 * quotes ("List name too long (max 80)"). Exported here so the server
 * throws and the web pre-validation can read the same numbers once
 * their tickets land (#109/#110) — never re-hardcode them.
 */
export const LIST_NAME_MAX = 80;
export const ITEM_TITLE_MAX = 200;

const UNCAUGHT_MARKER = "Uncaught Error:";
const CALLED_BY_CLIENT = /\s*Called by client\.?\s*$/;
const FRAME_HEAD = /^\[CONVEX M\([^\]]*\)\][^\n]*\n/;
const UNAUTHENTICATED = /^Unauthenticated\b/i;
/**
 * The auth provider's failure tokens, probed for real on a local
 * backend (#110): a wrong password is "InvalidSecret", an unknown
 * account is "InvalidAccountId". One anti-enumeration line covers
 * both — never reveal whether the account exists.
 */
const AUTH_FAILURE_TOKENS = /^(InvalidSecret|InvalidAccountId)\.?$/;
/** Framework-generated arg-validation text — never show it verbatim. */
const VALIDATOR_NOISE =
  /Invalid function arguments|ArgumentValidationError|Object contains extra field/i;

function mappedCopy(line: string): string | null {
  if (line === "") return GENERIC_ERROR_MESSAGE;
  if (UNAUTHENTICATED.test(line)) return SESSION_EXPIRED_MESSAGE;
  if (AUTH_FAILURE_TOKENS.test(line)) return AUTH_FAILURE_MESSAGE;
  if (VALIDATOR_NOISE.test(line)) return GENERIC_ERROR_MESSAGE;
  return null;
}

export function errorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  const raw = err.message;
  const frame = raw.match(FRAME_HEAD);
  const uncaught = raw.lastIndexOf(UNCAUGHT_MARKER);
  if (uncaught === -1 && !frame) {
    // Production shape: the bare message passes through unchanged —
    // the old caller-facing contract — except for the mapped shapes.
    const mapped = mappedCopy(raw.trim());
    return mapped ?? raw;
  }
  const cut =
    uncaught !== -1 ? uncaught + UNCAUGHT_MARKER.length : frame![0].length;
  const line = lineAt(raw, cut)
    .replace(/\s+/g, " ")
    .replace(CALLED_BY_CLIENT, "")
    .trim();
  return mappedCopy(line) ?? line;
}

/** The extracted region's own line — message proper, never stack noise. */
function lineAt(raw: string, from: number): string {
  const end = raw.indexOf("\n", from);
  return raw.slice(from, end === -1 ? raw.length : end);
}
